import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import PublicSurvey from "./PublicSurvey";
import ExternalRoomRecorder from "./ExternalRoomRecorder";
import PreviewPage from "./customer_intelligence/PreviewPage";
import CustomerHub from "./customer_intelligence/CustomerHub";
import MarketingGlossary from "./customer_intelligence/MarketingGlossary";
import FontPreview from "./customer_intelligence/FontPreview";
import DataSourcesPage from "./customer_intelligence/DataSourcesPage";
import { registerServiceWorker } from "./lib/pwa";
import { startAutoUpdate } from "./lib/autoUpdate";
import { installGlobalErrorReporting } from "./lib/clientErrorReporter";

installGlobalErrorReporting();

// ─── Chunk-load-error recovery (Feb 2026 — fixes white-screen on mobile) ─────
function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || "";
  const msg = String(err.message || err || "");
  return name === "ChunkLoadError"
    || /Loading chunk \d+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg);
}
function recoverFromStaleCache() {
  try {
    if (sessionStorage.getItem("tw_chunk_recover") === "1") return;
    sessionStorage.setItem("tw_chunk_recover", "1");
  } catch (e) { /* ignore */ }
  if ("caches" in window) {
    caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .finally(() => window.location.reload());
  } else {
    window.location.reload();
  }
}
window.addEventListener("error", (e) => {
  if (isChunkLoadError(e.error)) recoverFromStaleCache();
});
window.addEventListener("unhandledrejection", (e) => {
  if (isChunkLoadError(e.reason)) recoverFromStaleCache();
});
window.addEventListener("load", () => {
  setTimeout(() => { try { sessionStorage.removeItem("tw_chunk_recover"); } catch (e) { /* storage disabled — ignore */ } }, 3000);
});

const root = ReactDOM.createRoot(document.getElementById("root"));
const path = window.location.pathname;
const isInvitedSurvey = /^\/survey\/(?!open\/)[^/]+\/[^/]+\/?$/.test(path);
const isOpenSurvey = /^\/survey\/open\/[^/]+\/?$/.test(path);
const isPublicSurvey = isInvitedSurvey || isOpenSurvey;
const isExternalRoom = /^\/external\/room\/[^/]+\/?$/.test(path);
// Track B, Phase 3 — hidden Customer Intelligence preview (admin/ceo only,
// gated inside the component). Isolated from all production customer screens.
const isCIPreview = /^\/customer-intelligence-preview\/?$/.test(path);
// Track B, Phase 11 — hidden Customer Hub preview (5-tab restructure).
// Same isolation contract: nothing else in the app links to this route.
const isCHubPreview = /^\/customer-hub-preview\/?$/.test(path);
// v200.67 — Real, permanent route to the Unified Customer Hub tab
// inside <App/>. Redirects to `/?tab=customer_hub` BEFORE render so
// auth + role checks flow normally through <App/>. Same permission
// (`customers.view`) and same roles as the tab definition — no
// permission widening.
const isCHubReal = /^\/customer-hub\/?$/.test(path);
if (isCHubReal && typeof window !== "undefined") {
  window.history.replaceState(null, "", "/?tab=customer_hub");
}
// Track B, Phase 12a — Marketing Glossary preview (dictionary listing).
const isGlossaryPreview = /^\/marketing-glossary-preview\/?$/.test(path);
// Design-system font picker — isolated comparison of 3 candidates.
const isFontPreview = /^\/font-preview\/?$/.test(path);
// Phase 12 mandate #6 — public transparency page for every metric shown.
const isDataSources = /^\/data-sources\/?$/.test(path);
root.render(
  <React.StrictMode>
    {isExternalRoom
      ? <ExternalRoomRecorder />
      : (isDataSources
          ? <DataSourcesPage />
          : (isFontPreview
              ? <FontPreview />
              : (isGlossaryPreview
                  ? <MarketingGlossary />
                  : (isCHubPreview
                      ? <CustomerHub />
                      : (isCIPreview
                          ? <PreviewPage />
                          : (isPublicSurvey ? <PublicSurvey /> : <App />))))))}
  </React.StrictMode>,
);

// Register Service Worker for PWA + Push (after first render)
if (process.env.NODE_ENV === "production" || window.location.hostname !== "localhost") {
  registerServiceWorker(() => {
    window.dispatchEvent(new CustomEvent("thawany:sw-update-ready"));
  });
}

// v119.220 — فحص دوري مستقل عن الـ SW: أي نسخة جديدة تُحمَّل تلقائياً
startAutoUpdate();
